import { useState } from 'react';
import { 
  Home, 
  Users, 
  BookOpen, 
  DollarSign, 
  Package, 
  Tag, 
  Settings, 
  BarChart3, 
  FileText, 
  Shield, 
  Bell, 
  Activity,
  ChevronDown,
  ChevronUp,
  Crown,
  HardDrive,
  Flag,
  TrendingUp,
  Handshake
} from 'lucide-react';

interface AdminMobileDropdownProps {
  activeSection: string;
  setActiveSection?: (section: string) => void;
  onSectionChange?: (section: string) => void;
  systemType?: 'course' | 'salepage';
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: string;
  isExternal?: boolean;
  externalUrl?: string;
}

export default function AdminMobileDropdown({ 
  activeSection, 
  setActiveSection,
  onSectionChange,
  systemType = 'course'
}: AdminMobileDropdownProps) {
  const handleSectionChange = setActiveSection || onSectionChange || (() => {});
  const [isOpen, setIsOpen] = useState(false);

  const navItems: NavItem[] = [
    {
      id: 'overview',
      label: 'ภาพรวม',
      icon: Home,
    },
    {
      id: 'all-users',
      label: 'ผู้ใช้ทั้งหมด',
      icon: Users,
    },
    {
      id: 'instructors',
      label: 'ผู้สอน',
      icon: Crown,
    },
    {
      id: 'partners',
      label: 'Partners',
      icon: Handshake,
      isExternal: true,
      externalUrl: 'https://coursedee.web.app/admin/partners',
    },
    {
      id: 'all-courses',
      label: 'คอร์สทั้งหมด',
      icon: BookOpen,
    },
    {
      id: 'flagged-courses',
      label: 'คอร์สที่ถูกรายงาน',
      icon: Flag,
    },
    {
      id: 'total-revenue',
      label: 'รายได้รวม',
      icon: DollarSign,
    },
    {
      id: 'subscription-payments',
      label: 'การชำระเงิน',
      icon: FileText,
    },
    {
      id: 'payment-approval',
      label: 'อนุมัติการชำระเงิน',
      icon: Shield,
    },
    {
      id: 'subscription-management',
      label: 'จัดการ Subscription',
      icon: Crown,
    },
    {
      id: 'discount-management',
      label: 'จัดการส่วนลด',
      icon: Tag,
    },
    {
      id: 'storage-management',
      label: 'จัดการพื้นที่',
      icon: HardDrive,
    },
    {
      id: 'system-settings',
      label: 'ตั้งค่าระบบ',
      icon: Settings,
    },
    {
      id: 'activity-logs',
      label: 'บันทึกกิจกรรม',
      icon: Activity,
    },
    {
      id: 'system-health',
      label: 'สถานะระบบ',
      icon: Shield,
    },
    {
      id: 'notifications',
      label: 'การแจ้งเตือน',
      icon: Bell,
    },
    {
      id: 'analytics',
      label: 'วิเคราะห์ข้อมูล',
      icon: TrendingUp,
    },
    {
      id: 'usage-stats',
      label: 'สถิติการใช้งาน',
      icon: BarChart3,
    },
  ];

  const getCurrentLabel = () => {
    const currentItem = navItems.find(item => item.id === activeSection);
    return currentItem ? currentItem.label : 'ภาพรวม';
  };

  const getCurrentIcon = () => {
    const currentItem = navItems.find(item => item.id === activeSection);
    return currentItem ? currentItem.icon : Home;
  };

  const CurrentIcon = getCurrentIcon();

  return (
    <div className="lg:hidden">
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white/70 backdrop-blur-xl border border-white/40 rounded-2xl shadow-lg shadow-black/5"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600/10 to-pink-600/5 flex items-center justify-center">
            <CurrentIcon className="w-5 h-5 text-blue-700" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-gray-900">{getCurrentLabel()}</div>
            <div className="text-xs text-gray-500">เลือกหมวดหมู่</div>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-3 bg-white/80 backdrop-blur-xl border border-white/40 rounded-2xl shadow-xl shadow-black/10 overflow-hidden">
          <div className="max-h-96 overflow-y-auto p-2">
            {navItems.map(item => {
              const isActive = activeSection === item.id;
              const ItemIcon = item.icon;
              
              if (item.isExternal && item.externalUrl) {
                return (
                  <a
                    key={item.id}
                    href={item.externalUrl}
                    onClick={() => setIsOpen(false)}
                    className="relative w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all active:scale-[0.99] text-gray-700 hover:bg-white/70"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-transparent">
                        <ItemIcon className="w-5 h-5" />
                      </div>
                      <span className="font-medium">{item.label}</span>
                    </div>
                  </a>
                );
              }
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    handleSectionChange(item.id);
                    setIsOpen(false);
                  }}
                  className={`relative w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all active:scale-[0.99] ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-600/10 to-pink-600/5 text-blue-700'
                      : 'text-gray-700 hover:bg-white/70'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-1.5 rounded-r-full bg-gradient-to-b from-blue-600 to-pink-600" />
                    )}
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                      isActive ? 'bg-white/70 shadow-sm' : 'bg-transparent'
                    }`}>
                      <ItemIcon className="w-5 h-5" />
                    </div>
                    <span className="font-medium">{item.label}</span>
                  </div>
                  {item.badge && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
